// SD "On this page" list (<sd-toc>, markup in snippets/sd-toc.liquid): terms and legal content sections.
// Hooks, not classes: [data-toc] nav, [data-toc-link] links, [data-toggle] bar button, [data-toc-drawer] /
// [data-toc-panel] drawer, [data-toc-close] close button, [data-cur] current-section label.
// Links scroll smoothly to their section (instant with reduced motion) and one IntersectionObserver marks the
// section being read (aria-current). Below 990px the bar opens the list as a drawer from the left, the same way
// as the collection filter drawer (Dawn's trapFocus + overflow-hidden-tablet). No scroll listeners.
// A Theme Editor re-render inserts a fresh element, so it binds again.
if (!customElements.get('sd-toc')) {
  customElements.define(
    'sd-toc',
    class SdToc extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.toc = this.querySelector('[data-toc]');
        if (!this.toc) return;
        this.toggleBtn = this.toc.querySelector('[data-toggle]');
        this.drawer = this.toc.querySelector('[data-toc-drawer]');
        this.panel = this.toc.querySelector('[data-toc-panel]');
        this.cur = this.toc.querySelector('[data-cur]');
        // Keep only links whose section exists (a removed block never leaves a dead link)
        this.links = [...this.toc.querySelectorAll('[data-toc-link]')].filter((a) => {
          const ok = !!this.target(a);
          if (!ok) a.closest('li')?.remove();
          return ok;
        });
        if (!this.links.length) return;

        this.addEventListener('click', (e) => this.onClick(e));
        this.toc.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen) this.setOpen(false);
        });
        this.setActive(this.links[0]);
        this.watch();
      }

      disconnectedCallback() {
        this.io?.disconnect();
        if (this.isOpen) document.body.classList.remove('overflow-hidden-tablet');
      }

      get isOpen() {
        return this.toc.classList.contains('is-open');
      }

      target(a) {
        const id = decodeURIComponent((a.getAttribute('href') || '').slice(1));
        return id ? this.querySelector(`[id="${CSS.escape(id)}"]`) : null;
      }

      onClick(e) {
        if (e.target.closest('[data-toggle]')) return this.setOpen(true);
        // The close button, or the dimmed area around the panel
        if (e.target.closest('[data-toc-close]') || e.target === this.drawer) return this.setOpen(false);
        const a = e.target.closest('[data-toc-link]');
        if (!a) return;
        const el = this.target(a);
        if (!el) return;
        e.preventDefault();
        this.setOpen(false, false);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        el.focus({ preventScroll: true });
        history.replaceState(null, '', a.getAttribute('href'));
        this.setActive(a);
      }

      setOpen(open, returnFocus = true) {
        if (open === this.isOpen) return;
        this.toc.classList.toggle('is-open', open);
        this.toggleBtn?.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('overflow-hidden-tablet', open);
        if (open) {
          this.drawer?.setAttribute('role', 'dialog');
          this.drawer?.setAttribute('aria-modal', 'true');
          // Next frame: the drawer is visible by then, so focus lands
          requestAnimationFrame(() => {
            if (typeof trapFocus === 'function' && this.panel) trapFocus(this.panel);
          });
        } else {
          this.drawer?.removeAttribute('role');
          this.drawer?.removeAttribute('aria-modal');
          if (typeof removeTrapFocus === 'function') removeTrapFocus(returnFocus ? this.toggleBtn : null);
        }
      }

      setActive(link) {
        if (!link || link === this.active) return;
        this.active?.removeAttribute('aria-current');
        link.setAttribute('aria-current', 'location');
        this.active = link;
        if (this.cur) this.cur.textContent = (link.querySelector('[data-toc-txt]') || link).textContent.trim();
      }

      // The active section is the first one crossing a band near the top of the viewport
      watch() {
        if (!('IntersectionObserver' in window)) return;
        const byEl = new Map(this.links.map((a) => [this.target(a), a]));
        const inBand = new Set();
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => (en.isIntersecting ? inBand.add(en.target) : inBand.delete(en.target)));
            const first = [...byEl.keys()].find((el) => inBand.has(el));
            if (first) this.setActive(byEl.get(first));
          },
          { rootMargin: '-20% 0px -65% 0px' }
        );
        byEl.forEach((_, el) => this.io.observe(el));
      }
    }
  );
}
